import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Wifi, Users, Presentation, Waves, Coffee, MapPin, CheckCircle2, Briefcase } from 'lucide-react';
import SEO from '../components/SEO';

const SeminaireIleDeRe = () => {
  const faqs = [
    {
      q: "La Villa Nomade est-elle adaptée à un séminaire d'entreprise ?",
      a: "Oui. La villa dispose d'une grande table dans une pièce de vie lumineuse permettant de réunir et faire travailler une équipe, d'une connexion Wifi performante (150 Mbit/s) et d'un cadre calme et inspirant, idéal pour les petits séminaires de direction et sessions de team building."
    },
    {
      q: "Quelle est la capacité de la villa pour un séminaire ?",
      a: "La villa accueille jusqu'à 8 personnes en couchage (4 chambres, 3 salles de bain) et permet de réunir une dizaine de personnes autour de la grande table de travail pour vos réunions et ateliers."
    },
    {
      q: "Quelle est la qualité de la connexion internet ?",
      a: "La Villa Nomade bénéficie d'une connexion Wifi haut débit performante de 150 Mbit/s, adaptée aux visioconférences, au partage de fichiers et au travail collaboratif en ligne."
    },
    {
      q: "Quelle est la meilleure période pour organiser un séminaire à l'Île de Ré ?",
      a: "La moyenne saison (avril, mai, septembre, octobre) est idéale : tarifs attractifs, calme, disponibilité et un environnement propice à la concentration comme à la cohésion d'équipe, entre golf, plages et marais."
    }
  ];

  return (
    <>
      <SEO
        title="Séminaire d'Entreprise Île de Ré | Villa pour Réunions & Team Building - Villa Nomade"
        description="Organisez votre séminaire de direction ou team building à l'Île de Ré à la Villa Nomade : grande table de réunion, Wifi performant 150 Mbit/s, jusqu'à 8 couchages, spa de nage et cadre calme aux Portes-en-Ré. Idéal en moyenne saison."
        keywords="séminaire Île de Ré, séminaire entreprise Île de Ré, team building Île de Ré, villa séminaire Charente-Maritime, réunion direction Île de Ré, location villa professionnelle Ré, wifi villa Île de Ré"
        image="https://images.unsplash.com/photo-1633457896836-f8d6025c85d1?w=1200"
        url="/seminaire-ile-de-re"
      />

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1633457896836-f8d6025c85d1?w=1600)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 to-black/70"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <Briefcase className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Séminaires à l'Île de Ré
            </h1>
            <p className="text-lg sm:text-xl text-gray-200">
              Un cadre d'exception pour réunir, travailler et fédérer vos équipes
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Intro */}
        <section className="mb-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">La Villa Nomade, Votre Lieu de Séminaire Privé</h2>
          <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
            Sortez vos équipes du cadre habituel ! Aux Portes-en-Ré, la <strong>Villa Nomade</strong> offre un
            environnement <strong>calme, lumineux et inspirant</strong>, parfait pour vos <strong>petits séminaires
            de direction</strong>, comités et sessions de team building. Une grande table de travail, une connexion
            <strong> Wifi performante (150 Mbit/s)</strong> et le confort d'une villa haut de gamme réunis en un même lieu.
          </p>
        </section>

        {/* Atouts */}
        <section className="mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <Presentation className="w-10 h-10 text-amber-600 mb-3" />
              <h3 className="font-semibold mb-2">Grande table de réunion</h3>
              <p className="text-sm text-gray-600">Une vaste table dans la pièce de vie pour réunir et faire travailler jusqu'à 10 personnes.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <Wifi className="w-10 h-10 text-amber-600 mb-3" />
              <h3 className="font-semibold mb-2">Wifi 150 Mbit/s</h3>
              <p className="text-sm text-gray-600">Connexion haut débit fiable, idéale pour visioconférences et travail collaboratif.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <Users className="w-10 h-10 text-amber-600 mb-3" />
              <h3 className="font-semibold mb-2">Jusqu'à 8 couchages</h3>
              <p className="text-sm text-gray-600">4 chambres et 3 salles de bain pour héberger votre équipe sur place.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <Waves className="w-10 h-10 text-amber-600 mb-3" />
              <h3 className="font-semibold mb-2">Spa de nage</h3>
              <p className="text-sm text-gray-600">Un moment de détente et de cohésion après vos sessions de travail.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <Coffee className="w-10 h-10 text-amber-600 mb-3" />
              <h3 className="font-semibold mb-2">Cuisine équipée & terrasses</h3>
              <p className="text-sm text-gray-600">Pauses conviviales, repas d'équipe et déjeuners au soleil au jardin.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <MapPin className="w-10 h-10 text-amber-600 mb-3" />
              <h3 className="font-semibold mb-2">Cadre nature & golf</h3>
              <p className="text-sm text-gray-600">Golf à pied par le jardin, plages et marais pour vos activités de team building.</p>
            </div>
          </div>
        </section>

        {/* Espace de travail */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <img
              src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=900"
              alt="Équipe en séminaire de travail autour d'une grande table lumineuse"
              className="w-full h-[380px] object-cover rounded-lg shadow-lg"
            />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Un Espace de Travail Inspirant</h2>
              <p className="text-gray-700 mb-4">
                La grande pièce de vie ouverte, baignée de lumière naturelle, se prête parfaitement à l'animation
                d'un séminaire : ateliers, brainstorming, réunions de comité de direction ou formation en petit comité.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Grande table conviviale pour réunir l'équipe</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Wifi haut débit 150 Mbit/s pour visio et cloud</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Ambiance calme, loin des distractions</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Deux terrasses et jardin pour les pauses</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" /> Parking privé 3 places</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Pourquoi moyenne saison */}
        <section className="mb-16 bg-gradient-to-r from-stone-100 to-amber-50 p-8 rounded-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Le Séminaire Idéal en Moyenne Saison</h2>
          <p className="text-gray-700 mb-4">
            Organisez votre séminaire en <strong>avril, mai, septembre ou octobre</strong> : vous bénéficiez de
            <strong> tarifs privilégiés</strong>, d'une île au calme et d'un environnement propice à la concentration
            comme à la cohésion d'équipe. Entre deux sessions, profitez du golf, des plages sauvages et des balades
            à vélo (vélos mis à disposition, les loueurs étant fermés hors saison).
          </p>
          <Link to="/moyenne-saison-ile-de-re" className="text-amber-700 font-medium hover:underline">Découvrir l'Île de Ré en moyenne saison →</Link>
        </section>

        {/* FAQ (AEO) */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Questions Fréquentes</h2>
          <div className="space-y-4 max-w-4xl mx-auto">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <h3 className="font-semibold text-lg text-gray-900 mb-2">{f.q}</h3>
                <p className="text-gray-700">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-[#c17a3a] to-[#a66830] text-white p-12 rounded-lg text-center">
          <Briefcase className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Organisez votre Séminaire à la Villa Nomade</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Grande table • Wifi 150 Mbit/s • Jusqu'à 8 couchages • Cadre inspirant
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#c17a3a] hover:bg-gray-100">Demander un devis séminaire</Button>
            </Link>
            <Link to="/photos">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">Voir les photos</Button>
            </Link>
          </div>
        </section>
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}
      </script>
    </>
  );
};

export default SeminaireIleDeRe;
